import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  OPERATOR_LEGAL_NAME,
  OPERATOR_COMPANY_NUMBER,
  OPERATOR_REGISTERED_OFFICE,
  PRODUCT_NAME,
} from '@/legal/companyInfo';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl space-y-8 px-6 py-10 text-sm leading-relaxed sm:text-base">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Terms of Service</h1>
          <p className="mt-2 text-slate-600">Last updated: 9 May 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">1. Who we are</h2>
          <p>
            The website and software service known as <strong>{PRODUCT_NAME}</strong> is operated by{' '}
            <strong>{OPERATOR_LEGAL_NAME}</strong> (company number {OPERATOR_COMPANY_NUMBER}), with registered office
            at {OPERATOR_REGISTERED_OFFICE} (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">2. The service</h2>
          <p>
            {PRODUCT_NAME} provides tools for restaurants to take and manage table reservations, including optional
            features such as customer communications, deposits, and calendar integration. The exact features available
            to you depend on your plan, configuration, and third-party services you connect (for example payment or
            calendar providers).
          </p>
          <p>
            <strong>Beta and access.</strong> We may offer {PRODUCT_NAME} on a limited, invite-only, or waiting-list
            basis while we test and improve the service. We may refuse or delay onboarding, suspend access, or change
            availability for operational, security, or commercial reasons.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">3. Your account</h2>
          <p>
            You must provide accurate information and keep login credentials secure. You are responsible for activity
            under your account. You must be authorised to act for the restaurant or business you register.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">4. Acceptable use</h2>
          <p>You agree not to misuse the service, including by attempting to:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>access data or systems you are not entitled to;</li>
            <li>introduce malware, overload infrastructure, or probe for vulnerabilities without our written consent;</li>
            <li>use the service to send unlawful, harassing, or deceptive communications;</li>
            <li>resell or scrape the service in breach of these terms or applicable law.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">5. Customer bookings and your responsibilities</h2>
          <p>
            Where you use {PRODUCT_NAME} to take bookings from diners, you are responsible for your relationship with
            those customers, your compliance with food licensing, age restrictions, pricing, deposits, cancellations, and
            applicable consumer laws. We are a software provider and are not party to the contract between you and your
            guests except where we explicitly provide a separate service to you.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">6. Third-party services</h2>
          <p>
            Parts of {PRODUCT_NAME} rely on third parties (for example hosting, email delivery, payments, or Google
            Calendar). Their terms and privacy notices may also apply. We are not responsible for outages or acts of
            those third parties beyond what the law requires.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">7. Intellectual property</h2>
          <p>
            We and our licensors own the {PRODUCT_NAME} software, branding, and related materials. Subject to these
            terms, we grant you a non-exclusive, non-transferable right to use the service for your internal business
            purposes during your subscription or access period.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">8. Disclaimers</h2>
          <p>
            The service is provided on an &quot;as is&quot; and &quot;as available&quot; basis to the fullest extent
            permitted by law. We do not guarantee uninterrupted or error-free operation. Nothing in these terms
            excludes or limits liability that cannot be excluded or limited under English law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">9. Limitation of liability</h2>
          <p>
            To the extent permitted by law, we are not liable for any indirect or consequential loss, loss of profit,
            loss of goodwill, or loss of data. Our total aggregate liability arising out of or in connection with these
            terms or the service in any twelve-month period is limited to the fees you paid us for {PRODUCT_NAME} in
            that period, or if none were paid, one hundred pounds sterling (£100).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">10. Suspension and termination</h2>
          <p>
            We may suspend or terminate access if you breach these terms, if we must do so for legal or security
            reasons, or if we withdraw or materially change the service. You may stop using {PRODUCT_NAME} at any time.
            Provisions that by their nature should survive will survive termination.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">11. Changes</h2>
          <p>
            We may update these terms from time to time. We will publish the updated version on this page with a new
            &quot;Last updated&quot; date. Continued use after changes constitutes acceptance where permitted by law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">12. Governing law</h2>
          <p>
            These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive
            jurisdiction, except where mandatory consumer protections apply elsewhere.
          </p>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 text-slate-700">
          <h2 className="text-lg font-semibold text-slate-900">Operator details</h2>
          <p className="mt-2">
            <strong>{OPERATOR_LEGAL_NAME}</strong>
            <br />
            Company number: {OPERATOR_COMPANY_NUMBER}
            <br />
            Registered office: {OPERATOR_REGISTERED_OFFICE}
          </p>
          <p className="mt-3 text-xs text-slate-500">
            These terms are a practical summary for operators using {PRODUCT_NAME}. They are not tailored legal advice.
            For restaurant-specific obligations (licensing, employment, allergens, GDPR as controller of guest data,
            etc.), you should take professional advice.
          </p>
        </section>
      </article>
    </div>
  );
}
